import React, { useContext, useState, useEffect } from 'react';

import { DataGridDefaultProps, DataGridPropTypes } from 'src/components/DataGrid/propTypes';
import { getFiltering, setFiltering } from 'src/utils/data/filtering';
import { getShowDeleted, setShowDeleted } from 'src/utils/data/deleted';
import { TableMetaProvider } from 'src/utils/context/TableMetaContext';
import { useSetQueryParams } from 'src/hooks/utils';
import CurrentUserMetaContext from 'src/utils/context/CurrentUserMetaContext';
import DataGridHeader from 'src/components/DataGrid/DataGridHeader';
import DataGridFooter from 'src/components/DataGrid/DataGridFooter';
import DataGridTable from 'src/components/DataGrid/DataGridTable';
import Pagination from 'src/components/Pagination/Pagination';
import Styled from 'src/components/DataGrid/DataGrid.styles';

const DataGrid = ({
  data,
  footer,
  gridColumns,
  gridHeader,
  gridItem,
  header,
  hideDeletedButton,
  loading,
  name,
  onPageChanged,
  paginationUseQuery,
  refetch: refetchReal,
  title,
  variables,
  hideColumnFilter = false,
  ...rest
}) => {
  let currentUserMeta = null;
  if (header || name === 'JobsGrid') {
    currentUserMeta = useContext(CurrentUserMetaContext)[name];
  }

  const [refetchVariables, setRefetchVariables] = useState(variables);

  useEffect(() => {
    refetchReal(refetchVariables);
  }, [JSON.stringify(refetchVariables)]);

  const refetch = async (vars = {}) => {
    const newVars = {
      ...refetchVariables,
      ...vars,
    };
    setRefetchVariables(newVars);
  };

  const forceRefetch = () => {
    refetchReal(refetchVariables);
  };

  const [columns, setColumns] = useState([]);

  const [filters, setFiltersReal] = useState(() => {
    const filtering = getFiltering(name);

    if (filtering.length > 0) {
      refetch({
        columnFilters: filtering.map(({ column, field, value }) => ({
          field,
          value: (column?.filterType === 'company') ? JSON.stringify(value.ids) : JSON.stringify(value),
        })),
      });
    }

    return filtering;
  });

  const [filtersVisible, setFiltersVisible] = useState(filters.length > 0);

  const setFilters = newFilters => {
    setFiltersReal(newFilters);
    useSetQueryParams(window.location, { page: 1 });

    setFiltering(name, newFilters);
    setFiltersVisible(newFilters.length > 0);
  };

  const [withDeleted, setWithDeletedReal] = useState(
    getShowDeleted(name) || false,
  );
  const setWithDeleted = newValue => {
    setWithDeletedReal(newValue);

    setShowDeleted(name, newValue);
  };
  useEffect(() => {
    refetch({ withDeleted });
  }, [withDeleted]);

  let headerEl = null;
  if (header) {
    headerEl = header();
  }

  return (
    <Styled.Grid {...rest}>
      <TableMetaProvider
        value={{
          columns,
          filters,
          filtersVisible,
          forceRefetch,
          hideDeletedButton,
          meta: currentUserMeta,
          name,
          refetch,
          setColumns,
          setFilters,
          setFiltersVisible,
          setWithDeleted,
          variables,
          withDeleted,
          hideColumnFilter,
        }}
      >
        {!header && <DataGridHeader title={title} />}
        {headerEl &&
          React.cloneElement(headerEl, {
            ...headerEl.props,
            onSearch: q => refetch({ q }),
          })}

        {data && data.items && (
          <DataGridTable
            {...rest}
            columns={gridColumns}
            header={gridHeader}
            item={gridItem}
            items={data.items}
            loading={loading}
          />
        )}

        {!footer && data && data.pageInfo && (
          <DataGridFooter>
            <Pagination
              pageNeighbours={1}
              refetch={(name === 'JobsGrid') ? refetchReal : refetch}
              totalRecords={data.pageInfo.totalCount}
              useQuery={paginationUseQuery}
            />
          </DataGridFooter>
        )}
        {footer && footer()}
      </TableMetaProvider>
    </Styled.Grid>
  );
};

DataGrid.propTypes = DataGridPropTypes;
DataGrid.defaultProps = DataGridDefaultProps;

export default React.memo(DataGrid);
